import React, { useTransition } from 'react'

import { useTranslation } from '@disco/i18n/use-translation'
import { clientLibraryUpdate } from '@disco/state-management/actions/client-library-list-actions'

import type { ClientLibrary } from 'types/client-library'

import Row from 'components/common/row'
import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'
import PageContainer from 'components/ui/page-container'
import TextField from 'components/ui/text-field'

import { useFormState } from 'hooks/use-form-state'
import { useDispatch } from 'hooks/use-redux'

const FORM_FIELDS = ['description', 'contact_details', 'display_name'] as const

const ArtistProfileContact = ({
  id,
  loading,
  formFields,
}: {
  id: number
  loading: boolean
  formFields: Pick<
    ClientLibrary,
    'description' | 'contact_details' | 'display_name'
  >
}): React.ReactNode => {
  const dispatch = useDispatch()
  const { t } = useTranslation('common')
  const [formState, setFormState, pendingChanges] = useFormState(
    formFields,
    FORM_FIELDS,
  )
  const [updatePending, startUpdateTransition] = useTransition()

  const handleSubmit = (event: React.SyntheticEvent<HTMLButtonElement>) => {
    event.preventDefault()
    startUpdateTransition(() =>
      dispatch(
        clientLibraryUpdate(id, formState, {
          successMessage: t('common:artistUpdated', 'Artist updated!'),
        }),
      ),
    )
  }

  return (
    <PageContainer.Section
      title={t('common:manageArtistDetails', 'Manage artist details')}
    >
      <Row columnsSize={[6]}>
        <div>
          <TextField
            disabled={loading}
            label={t('common:name', 'Name')}
            name="display_name"
            placeholder={t('common:yourArtistName', 'Your artist name')}
            value={formState['display_name'] || ''}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setFormState('display_name', event.target.value)
            }
            required
            error={
              formState['display_name']
                ? ''
                : t(
                    'common:yourArtistNameIsRequired',
                    'Your artist name is required',
                  )
            }
          />
        </div>
      </Row>
      <Row columnsSize={[6]}>
        <div>
          <TextField
            disabled={loading}
            label={t('common:about', 'About')}
            multiline={5}
            name="description"
            placeholder={t(
              'common:enterInformationAboutTheArtistHere',
              'Enter information about the artist here.',
            )}
            value={formState['description'] || ''}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setFormState('description', event.target.value)
            }
          />
        </div>
      </Row>
      <Row columnsSize={[6]}>
        <div>
          <TextField
            disabled={loading}
            label={t('common:contactDetails', 'Contact details')}
            multiline={5}
            name="contact_details"
            placeholder={t(
              'common:enterYourContactDetailsHere',
              'Enter your contact details here.',
            )}
            value={formState['contact_details'] || ''}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setFormState('contact_details', event.target.value)
            }
          />
        </div>
      </Row>
      <ButtonContainer>
        <Button
          disabled={
            loading ||
            !pendingChanges ||
            updatePending ||
            !formState['display_name']
          }
          appearance="primary"
          caption={t('common:save', 'Save')}
          onClick={handleSubmit}
        />
      </ButtonContainer>
    </PageContainer.Section>
  )
}

export default ArtistProfileContact
